import Badge from '../../components/base/Badge';
import AdvanceTable from '../../components/base/AdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import { Link } from 'react-router-dom';
import { Button, Modal, ProgressBar } from 'react-bootstrap';
import {
  faArrowUp,
  faArrowDown,
  faTrash,
  faScissors,
  faFlagCheckered
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { http } from '../../features/jobs/api/http';
import { useMemo, useState } from 'react';
import { useOrders } from '../../features/orders/useOrders';
import { gySuffix } from '../../helpers/productLabel';
import { API } from '../../config/api';
import {
  buildClientIndex,
  deriveDestination,
  destinationBadge
} from '../../helpers/orderDestination';
// `GB::phase::kind::value` → what a group-by run is grouped by. Same parser the
// queue editor and the Factory-3D panel use, so the three cannot disagree about
// what counts as a run — see helpers/machineQueueBlocks.js.
import {
  parseGroupByKey,
  buildTaskOrder
} from '../../helpers/machineQueueBlocks.js';
import { readableInk } from '../../helpers/contrastInk';
// The drums of one run, and the repairs the length ledger cannot make — cancel,
// merge, split, move. Reached from the Drum Ledger page as well; the queue is
// simply where a planner already is when a drum change goes unnoticed.
import DrumSurgeryModal from '../machines/DrumSurgeryModal';
// Closing the drum on the machine by hand — the drum end the line never saw.
import ForceCompleteModal from '../machines/ForceCompleteModal';
import { clientColour } from '../../helpers/clientColour';
// One bobbin under several products. Same rule Project Token State uses, so the
// two screens cannot disagree about which drums are shared.
import {
  buildSharedTokenIndex,
  sharedPartners
} from '../../helpers/sharedTokens';

const fmt = n => Number(n || 0).toLocaleString();

// The run-position chip is painted the conductor's colour — but a rope, a
// sheath or a coiling pass has no conductor colour, and those fell back to the
// border grey, which reads as "disabled" rather than "position 3".
//
// The fallback is keyed on the POSITION instead, which is what the number says:
// two rows carrying the same number are one pass, and now they carry the same
// colour as well. Saturated mid-tones only, so `readableInk` has a real choice.
const RUN_POS_COLOURS = [
  '#3874ff', '#0fc6c2', '#e5780b', '#8b5cf6',
  '#f43f5e', '#10b981', '#6366f1', '#ec4899'
];
const runPosColour = pos => {
  const n = Number(pos);
  if (!Number.isFinite(n) || n < 1) return '#6366f1';
  return RUN_POS_COLOURS[(n - 1) % RUN_POS_COLOURS.length];
};

function shortId(id, n = 5) {
  if (!id) return '—';
  return String(id).slice(0, n);
}

// From the shared vocabulary — see helpers/orderDestination.js. Only the mixed
// label is this screen's own: "Client + Stock" says more here than "Mixed".
function destBadge(d) {
  if (d === 'CLIENT') return destinationBadge('CLIENT');
  if (d === 'MIXED') return { ...destinationBadge('MIXED'), label: 'Client + Stock' };
  return destinationBadge('STOCK');
}

// Final-phase delivery routing badge (English, for our UI). Mirrors the operator
// HMI badge. Shows for final phases (exterior_sheat / coiling / rewinding);
// defaults to FINAL PRODUCT when a final-phase task carries no explicit mode.
const DELIVERY_FINAL_PHASES = ['exterior_sheat', 'coiling', 'rewinding'];
function deliveryBadge(mode, phase) {
  const m = String(mode || '').toLowerCase();
  if (m === 'coils_on_machine') return { bg: 'info', label: 'COILS ON MACHINE' };
  if (m === 'to_coiling') return { bg: 'primary', label: 'TO COILING' };
  if (m === 'to_rewinding') return { bg: 'primary', label: 'TO REWINDING' };
  if (m === 'final_product') return { bg: 'success', label: 'FINAL PRODUCT' };
  if (DELIVERY_FINAL_PHASES.includes(String(phase || '').toLowerCase()))
    return { bg: 'success', label: 'FINAL PRODUCT' };
  return null;
}

function isDoneStatus(status) {
  const s = String(status || '').toUpperCase();
  return s === 'DONE' || s === 'COMPLETED' || s === 'FINISHED';
}

function statusBg(status) {
  const s = String(status || 'UNKNOWN').toUpperCase();
  if (s === 'RUNNING') return 'success';
  if (s === 'PENDING') return 'warning';
  if (s === 'DONE' || s === 'COMPLETED' || s === 'FINISHED') return 'success';
  if (s === 'FAILED') return 'danger';
  return 'secondary';
}

function StatusBadge({ status }) {
  const s = String(status || 'UNKNOWN').toUpperCase();
  return (
    <Badge variant="phoenix" bg={statusBg(s)}>
      {s}
    </Badge>
  );
}

// simple map for the common colours (extend as needed)
function mapColorToCss(c) {
  const s = String(c || '').toUpperCase();
  if (!s) return null;

  const m = {
    BLUE: '#2f7cff',
    BROWN: '#8b5a2b',
    BLACK: '#222222',
    GREY: '#7a7a7a',
    GRAY: '#7a7a7a',
    'GREEN-YELLOW': '#9acd32',
    GREEN: '#2e8b57',
    YELLOW: '#f0c400',
    RED: '#d9534f',
    WHITE: '#e5e5e5',
    ORANGE: '#f39c12'
  };

  return m[s] || null;
}

function pct(produced, planned) {
  const p = Number(produced || 0);
  const pl = Number(planned || 0);
  if (!pl) return null;
  return Math.max(0, Math.min(100, Math.round((p / pl) * 100)));
}

function QueueRows({
  jobId,
  machineId,
  simulateExecution,
  queue = [],
  sharedIndex,
  taskOrder,
  onSurgery,
  onForceComplete,
  onConfirm,
  productLabel
}) {
  if (!queue.length) return <span className="text-700">—</span>;

  return (
    <div className="d-flex flex-column gap-2">
      {queue.map((task, index) => {
        const taskBorder = mapColorToCss(task.color);
        const taskPlanned = task.plannedLength ? `${task.plannedLength}m` : '—';
        const taskPhase = task.phase || '—';
        const taskIdAxios = task.taskId;

        // Unwinding (derulare): show the wire being paid off on the right.
        // Per-task from the COPPER entry of materialPlan — machine-agnostic
        // (works for any machine on which an unwinding is launched).
        const isUnwinding = String(task.phase || '').toLowerCase() === 'unwinding';
        const copperWire = isUnwinding
          ? (Array.isArray(task.materialPlan) ? task.materialPlan : []).find(
              m => String(m?.material || '').toUpperCase() === 'COPPER'
            )
          : null;

        const isFirst = index === 0;
        const isLast = index === queue.length - 1;

        // Tandem (fused interior_sheat + exterior_sheat) → ONE card, phase names
        // joined with " + " (interior_sheat + exterior_sheat).
        const displayPhase =
          task.tandem &&
          Array.isArray(task.tandemPhases) &&
          task.tandemPhases.length
            ? task.tandemPhases.join(' + ')
            : taskPhase;

        const delivery = deliveryBadge(
          task.deliveryMode || task.deliveryNote,
          taskPhase
        );

        // The two things this per-product table could not say on its own.
        //
        // MERGE: another product writes to this task's own output token — one
        // drum, one pass, listed twice because the table is per product.
        // GROUPED RUN: this task was launched with group-by colour / wire Ø, so
        // it is queued next to its siblings deliberately and reordering it
        // alone would break the run. Each drum is still its own token and its
        // own bobbin — a run is not a merge.
        const mergePartners = (() => {
          const out = [];
          for (const o of task.outputs || []) {
            for (const label of sharedPartners(sharedIndex, o?.tokenId, jobId)) {
              if (!out.includes(label)) out.push(label);
            }
          }
          return out;
        })();

        const groupBy = parseGroupByKey(task.groupByKey);

        // ONE TASK, SEVERAL DRUMS. `split-drum` cuts a planned drum in two and
        // both halves stay on the same task, so the task gets two queue
        // positions — which read as a duplicated row. Reported 2026-10-30 after
        // splitting a rope in two. Saying which drum it is makes the pair read
        // as what it is: one run wound onto two bobbins.
        const sameTask = (queue || []).filter(
          t => String(t.taskId) === String(task.taskId)
        );
        const drumCount = sameTask.length;
        const drumIdx = sameTask.indexOf(task) + 1;

        const runPos = taskOrder?.map?.get(String(task.taskId)) ?? null;
        const runTotal = taskOrder?.total ?? 0;

        return (
          <div
            key={task.taskId}
            className="d-flex align-items-start justify-content-between gap-3 p-2 rounded-2"
            style={{
              border: `1px solid ${taskBorder || 'var(--phoenix-border-color)'}`,
              background: 'var(--phoenix-body-bg)'
            }}
            title={`${task.taskId} • ${displayPhase} • ${task.machineId || ''}`}
          >
            {/* LEFT: task meta */}
            <div className="d-flex flex-column" style={{ minWidth: 260 }}>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {/* Where this sits in the machine's real order. Two rows
                    carrying the same number are one pass — which is the whole
                    of "merge execution" said without jargon. */}
                {runPos != null && (
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold fs-10 flex-shrink-0"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: taskBorder || runPosColour(runPos),
                      // The chip is painted the conductor's colour, and white
                      // is not automatically readable on one: WHITE is #e5e5e5
                      // and YELLOW is #f0c400. Measured, not eyeballed.
                      color: readableInk(taskBorder || runPosColour(runPos))
                    }}
                    title={`Runs ${runPos}${runTotal ? ` of ${runTotal}` : ''} on ${machineId || 'this machine'}${
                      mergePartners.length
                        ? ` — together with ${mergePartners.join(', ')}`
                        : ''
                    }`}
                  >
                    {runPos}
                  </span>
                )}
                <span className="fw-semibold">{displayPhase}</span>
                <span className="text-700">• {taskPlanned}</span>
                {drumCount > 1 && (
                  <span
                    className="text-700 fs-10"
                    title={`This run is wound onto ${drumCount} bobbins — it was split. Each one is its own queue position.`}
                  >
                    • drum {drumIdx} of {drumCount}
                  </span>
                )}

                {task.color ? (
                  <span
                    className="px-2 py-0 rounded-2 fs-10 fw-semibold"
                    style={{
                      border: `1px solid ${taskBorder || 'var(--phoenix-border-color)'}`,
                      color: taskBorder || 'inherit'
                    }}
                  >
                    {task.color}
                  </span>
                ) : null}

                <span className="fs-10 text-700">• {task.taskId}</span>
              </div>

              {/* optional: status/progress per task */}
              <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                <StatusBadge status={task.status} />
                {delivery && (
                  <Badge variant="phoenix" bg={delivery.bg} className="fs-10">
                    {delivery.label}
                  </Badge>
                )}

                {mergePartners.length > 0 && (
                  <Badge
                    variant="phoenix"
                    bg="warning"
                    className="fs-10"
                    title={`One drum, one pass, shared with: ${mergePartners.join(', ')}`}
                  >
                    merge execution · with {mergePartners.join(', ')}
                  </Badge>
                )}

                {groupBy && (
                  <Badge
                    variant="phoenix"
                    bg="info"
                    className="fs-10"
                    title="Launched as a group-by run — queued next to its siblings so the machine does not change over. Each drum is still its own token."
                  >
                    grouped run ·{' '}
                    {groupBy.kind === 'color' ? groupBy.value : `Ø${groupBy.value}`}
                  </Badge>
                )}

                <span className="fs-10 text-700">{task.progress ?? 0}%</span>
              </div>

              {/* optional: inputs quick hint */}
              {Array.isArray(task.inputs) && task.inputs.length ? (
                <div className="fs-10 text-700 mt-1">
                  Inputs:{' '}
                  {task.inputs.map((i, idx) => (
                    <span key={i.tokenId || idx}>
                      {i.tokenId || '—'}
                      {idx < task.inputs.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            {/* RIGHT: wire (unwinding) + outputs tokens inline */}
            <div className="d-flex flex-wrap gap-2 justify-content-end align-items-start">
              {copperWire && (
                <div
                  className="px-2 py-1 rounded-2"
                  style={{
                    border: '1px solid var(--phoenix-warning)',
                    minWidth: 150,
                    background: 'var(--phoenix-body-bg)'
                  }}
                  title="Wire being unwound"
                >
                  <div className="fs-10 fw-bold text-warning">WIRE</div>
                  <div className="fs-9 fw-semibold">
                    {String(copperWire.wireType || 'COPPER').toUpperCase()}
                  </div>
                  {copperWire.wireDiameter != null && (
                    <div className="fs-10 text-700">
                      Ø {Number(copperWire.wireDiameter).toFixed(2)} mm
                    </div>
                  )}
                </div>
              )}
              {(task.outputs || []).map(tok => {
                const color = mapColorToCss(tok.color || task.color);
                const done =
                  isDoneStatus(tok.status) ||
                  tok.progress >= 100 ||
                  (tok.plannedLength &&
                    tok.producedLength >= tok.plannedLength);

                const pl = tok.plannedLength ?? task.plannedLength ?? null;
                const pr = Number(tok.producedLength.toFixed(2)) ?? 0;
                const percent = pct(pr, pl);

                return (
                  <div
                    key={tok.tokenId}
                    className="px-2 py-1 rounded-2"
                    style={{
                      border: `1px solid ${
                        color ||
                        (done
                          ? 'var(--phoenix-success)'
                          : 'var(--phoenix-danger)')
                      }`,
                      minWidth: 160,
                      background: 'var(--phoenix-body-bg)'
                    }}
                    title={`Token ${tok.tokenId} • ${tok.status || 'N/A'} • ${
                      pr
                    }/${pl || 0} • ${percent ?? tok.progress ?? 0}%`}
                  >
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <span className="fw-semibold fs-10">{tok.tokenId}</span>
                      <span
                        className="fs-10 px-2 rounded-2"
                        style={{
                          border: `1px solid ${
                            done
                              ? 'var(--phoenix-success)'
                              : 'var(--phoenix-danger)'
                          }`,
                          color: done
                            ? 'var(--phoenix-success)'
                            : 'var(--phoenix-danger)'
                        }}
                      >
                        {done ? 'DONE' : 'PENDING'}
                      </span>
                    </div>

                    <div className="d-flex align-items-center justify-content-between">
                      <span className="fs-10 text-700">
                        {pl ? `${pr}/${pl}m` : `${pr}m`}
                      </span>
                      <span className="fs-10 text-700">
                        {percent != null
                          ? `${percent}%`
                          : `${tok.progress ?? task.progress ?? 0}%`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ACTIONS */}
            <div className="d-flex align-items-center gap-2 ms-auto">
              {/* The drums of this run, and the four repairs. Here because this
                  is where a planner is standing when the line tells them a drum
                  change was missed: the queue is the screen that disagrees with
                  the floor first. */}
              <Button
                size="sm"
                variant="outline-info"
                title="Drums of this run — split one that is really two, merge, cancel or move"
                className="d-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34, padding: 0 }}
                onClick={() => onSurgery?.({ jobId, taskId: task.taskId })}
              >
                <FontAwesomeIcon icon={faScissors} />
              </Button>

              {/* On the drum that is RUNNING, and on any that has not started.
                  Running: the drum end the line never registered — the pinoles
                  opened, the bobbin came off, no sensor said so. Pending: a drum
                  that was made and never tracked, or one the planner is closing
                  out at the length that actually exists. The two take different
                  paths — a pending drum has no counter and no operators, so its
                  length has to be stated — and the modal switches on the status
                  it is given. The opposite of the bin either way: that one says
                  the drum was never made at all. */}
              {isDoneStatus(task.status) === false && (
                  <Button
                    size="sm"
                    variant="outline-success"
                    title={
                      String(task.status || '').toUpperCase() === 'ACTIVE'
                        ? 'Finish this drum now, at the length it really holds, and let the queue move on'
                        : 'Close this drum without running it, at a length you state'
                    }
                    className="d-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34, padding: 0 }}
                    onClick={() =>
                      onForceComplete?.({
                        machineId,
                        jobId,
                        productLabel,
                        taskId: task.taskId,
                        tokenId: (task.outputs && task.outputs[0]?.tokenId) || null,
                        status: String(task.status || '').toUpperCase(),
                        produced: Number(task.outputs?.[0]?.producedLength) || 0,
                        planned:
                          Number(task.outputs?.[0]?.plannedLength ?? task.plannedLength) || 0
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faFlagCheckered} />
                  </Button>
                )}

              <Button
                size="sm"
                variant="outline-danger"
                title="Remove from queue"
                className="d-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34, padding: 0 }}
                onClick={async () => {
                  // `http`, not fetch: a raw fetch carries no token — :5100
                  // refused both of these with 401 and the only sign was a line
                  // in the server log — and it misses the 401/403 handling every
                  // other call in the app goes through.
                  const body = {
                    jobId,
                    taskId: task.taskId,
                    tokenId: (task.outputs && task.outputs[0]?.tokenId) || null
                  };

                  // ASK THE SERVER WHAT WOULD HAPPEN, FIRST. Deleting a drum
                  // takes everything planned off it — a rope's sheaths, on a
                  // different machine's queue — so the confirm names them.
                  let dry;
                  try {
                    dry = await http.delete(
                      `/api/redis/queues/${machineId}/item`,
                      { data: { ...body, dryRun: true } }
                    );
                  } catch (err) {
                    // 409 is the refusal: produced work downstream, or something
                    // on a machine. Its message carries the list.
                    const msg =
                      err?.response?.data?.error || err.message || 'Request failed';
                    onConfirm?.({
                      title: 'Cannot remove this drum',
                      variant: 'danger',
                      body: msg,
                      confirmLabel: null
                    });
                    return;
                  }

                  // DID THE SERVER ACTUALLY HONOUR THE DRY RUN?
                  //
                  // A :5100 older than this flag ignores it and performs the
                  // REAL delete, so the drum would be gone before the confirm
                  // was ever shown. Caught rather than assumed — version skew
                  // between a reloaded browser and a not-yet-restarted server is
                  // the ordinary state of this machine during a work session.
                  if (dry?.data?.dryRun !== true || dry?.data?.applied !== false) {
                    onConfirm?.({
                      title: 'The server is running older code',
                      variant: 'warning',
                      body:
                        'It ignored the "ask first" flag, which means it may have ' +
                        'already removed this drum. Restart redis_dev and reload ' +
                        'before using this button again, then check the queue.',
                      confirmLabel: null
                    });
                    return;
                  }

                  const cascade = dry?.data?.cascade || [];
                  const unmerges = dry?.data?.unmerges || [];

                  onConfirm?.({
                    title: 'Remove this drum from the queue?',
                    variant: cascade.length ? 'warning' : 'primary',
                    body: `${displayPhase} · ${task.taskId}${
                      task.color ? ` · ${task.color}` : ''
                    }`,
                    lines: [
                      ...cascade.map(
                        c =>
                          `Also removes: ${String(c.phase || '?').replaceAll('_', ' ')}` +
                          `${c.machineId ? ` on ${String(c.machineId).toUpperCase()}` : ''}` +
                          `${c.tokenId ? ` (${c.tokenId})` : ''}`
                      ),
                      ...unmerges.map(
                        u =>
                          `Un-merges ${u.tokenId}: the other product keeps the bobbin ` +
                          `and stops waiting for this half.`
                      )
                    ],
                    confirmLabel: cascade.length
                      ? `Remove ${cascade.length + 1} drums`
                      : 'Remove',
                    onConfirm: async () => {
                      const res = await http.delete(
                        `/api/redis/queues/${machineId}/item`,
                        { data: body }
                      );
                      console.log(
                        'deleted from queue',
                        machineId,
                        task.taskId,
                        res?.data?.cascaded?.length
                          ? `+ ${res.data.cascaded.length} downstream`
                          : ''
                      );
                    }
                  });
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>

              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => {
                  // get tokenId from first output of this queue entry
                  const tokenId = task.outputs?.[0]?.tokenId || null;

                  // ASKS FIRST now. This writes PRODUCTION — it closes the drum
                  // at its planned length, draws down everything it consumes and
                  // moves the queue on — and it sat next to the delete button
                  // with no confirmation at all.
                  onConfirm?.({
                    title: 'Simulate this execution?',
                    variant: 'warning',
                    body: `${displayPhase} · ${task.taskId}${
                      task.color ? ` · ${task.color}` : ''
                    }`,
                    lines: [
                      'This writes real production: the drum is closed at its ' +
                        'planned length, what it consumes is drawn down, and the ' +
                        'queue moves on.',
                      'Use it for testing, not on a lot somebody is making.'
                    ],
                    confirmLabel: 'Simulate',
                    onConfirm: async () => {
                      await http.post('/api/redis/simulate-execution', {
                        jobId,
                        taskId: task.taskId,
                        tokenId
                      });
                    }
                  });
                }}
              >
                SIMULATE EXECUTION
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const MachineQueueTable = ({ rows = [], machineId, simulateExecution }) => {
  const { data: ordersData } = useOrders();
  // The run whose drums are being repaired, or null. Held here rather than per
  // row so only one is ever open.
  const [surgery, setSurgery] = useState(null);
  // The running drum a planner has asked to close by hand, or null.
  const [forceTarget, setForceTarget] = useState(null);
  // One confirmation at a time, for the two buttons that change production:
  // remove-from-queue and simulate-execution. Asked for 2026-10-30 — both used
  // to fire on a bare `window.confirm`, or on nothing at all.
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmErr, setConfirmErr] = useState('');
  const clientIndex = useMemo(
    () => buildClientIndex(ordersData || []),
    [ordersData]
  );

  // Which output tokens more than one product writes to.
  //
  // A merge-execution is two products extruded onto ONE drum in a single pass,
  // so the same tokenId appears under both — and this table, being per product,
  // lists them in separate rows with nothing saying they are the same physical
  // run. Built across every group on purpose: the partner is by definition in
  // another row, so no row can work it out alone.
  const sharedIndex = useMemo(() => {
    const entries = [];
    for (const g of rows || []) {
      for (const t of g?.queue || []) {
        for (const o of t?.outputs || []) {
          entries.push({
            tokenId: o?.tokenId,
            jobId: g?.jobId,
            productLabel: g?.productLabel || g?.name
          });
        }
      }
    }
    // Keyed by JOB, not by product label. Two separate lots of "CYYF 3x2.5"
    // have the same name and nothing to do with each other — collapsing them by
    // label made a real merge between them look like one product holding its
    // own drum, and the badge never appeared.
    return buildSharedTokenIndex(entries);
  }, [rows]);

  // Where each task sits in the order the machine will actually run it.
  //
  // The table is per product, so "what runs first" is the one thing it cannot
  // show: each row lists its own tasks in its own order and nothing relates the
  // rows to each other. An experienced planner reads it off the statuses; a new
  // one has no way in.
  //
  // Numbered by BLOCK, not by task, and that is the point: a merge-execution is
  // one pass of the machine, so both of its tasks carry the SAME number — two
  // rows showing "#4" is "these two run together", without having to know what
  // a shared token is.
  const taskOrder = useMemo(() => buildTaskOrder(rows), [rows]);

  const machineQueueColumns = [
    {
      id: 'job',
      header: 'Job',
      cell: ({ row: { original } }) => {
        const jobProgress = Math.round(
          original.queue.flatMap(t => t.outputs || []).map(o => o.progress)
        );

        // What this product is doing ON THIS MACHINE. Running beats done beats
        // queued, so one started task makes the block read ACTIVE and a block
        // whose work is all finished reads DONE — both of which are about this
        // queue, which is the only thing the screen can speak for.
        const statusOnThisMachine = (() => {
          const here = (original.queue || []).map(t =>
            String(t.status || '').toUpperCase()
          );
          if (!here.length) return original.status;
          if (here.some(st => ['ACTIVE', 'RUNNING', 'IN_PROGRESS'].includes(st)))
            return 'ACTIVE';
          if (here.every(st => ['DONE', 'COMPLETED', 'EXECUTED', 'FINISHED'].includes(st)))
            return 'COMPLETED';
          return 'PENDING';
        })();

        const dest = deriveDestination(
          original.allocations,
          original.plannedLength,
          clientIndex
        );
        const db = destBadge(dest.destination);

        return (
          <div className="d-flex flex-column gap-1">
            <Link
              to={`/apps/project-management/project-details/${original.jobId}`}
              className="fw-bold text-decoration-none"
            >
              {original.productLabel}
            </Link>

            <div className="fs-10 text-700">JOB-{shortId(original.jobId)}</div>

            <div className="fs-10 text-700">
              {original.numberOfConductors}C • {original.section}
              {gySuffix(original.section, original.greenYellowSection)} mm²
            </div>

            {/* order destination: client vs stock */}
            <div className="d-flex flex-column gap-1 mt-1">
              {/* The customer's NAME, not the word "Client" — the name is the
                  thing anybody scanning this column is looking for, and it was
                  repeated underneath in plain text anyway. A stable colour per
                  customer makes two adjacent rows for different ones obvious. */}
              {dest.clients.length > 0 ? (
                <div className="d-flex flex-wrap gap-1">
                  {dest.clients.map(name => (
                    <Badge
                      key={name}
                      bg={clientColour(name)}
                      variant="phoenix"
                      className="fs-10"
                      title={db.label}
                    >
                      {name}
                    </Badge>
                  ))}
                  {dest.destination === 'MIXED' && (
                    <Badge bg="warning" variant="phoenix" className="fs-10">
                      + Stock
                    </Badge>
                  )}
                </div>
              ) : (
                <Badge bg={db.bg} variant="phoenix" className="fs-10 align-self-start">
                  {db.label}
                </Badge>
              )}
              {dest.destination === 'MIXED' && (
                <div className="fs-10 text-700">
                  {fmt(dest.allocatedToOrders)} m client · {fmt(dest.stockMeters)}{' '}
                  m stock
                </div>
              )}
              {dest.destination === 'CLIENT' && (
                <div className="fs-10 text-700">
                  {fmt(dest.allocatedToOrders)} m reserved
                </div>
              )}
              {dest.destination === 'STOCK' && (
                <div className="fs-10 text-700">
                  {fmt(dest.stockMeters)} m to stock
                </div>
              )}
              {dest.dueEarliest && (
                <div className="fs-10 text-700">
                  Due {String(dest.dueEarliest).slice(0, 10)}
                </div>
              )}
            </div>

            <div>
              {/* THE STATUS ON THIS MACHINE, not the job's own.
                  `original.status` is the whole product's: a CYYF 3x1.5 whose
                  isolation is running on l75 reads ACTIVE on dtb1250 too, where
                  its rope is merely queued. Reported 2026-10-30 — "le pune
                  amandoua produsele active … si ele nu sunt active, CYYF 3x2.5 e
                  activ la linie" — with two products badged ACTIVE on a machine
                  running a third.
                  Derived from the tasks THIS queue holds, so the badge answers
                  the question the screen is asking. */}
              <StatusBadge status={statusOnThisMachine} />
            </div>
            <div className="mt-6">
              <ProgressBar
                now={jobProgress}
                style={{ height: 6 }}
                className="bg-body-tertiary"
              />
            </div>
          </div>
        );
      },
      meta: { headerProps: { style: { width: '20%' } } }
    },
    {
      id: 'queue',
      header: 'Queue (tasks → tokens)',
      cell: ({ row: { original } }) => (
        <QueueRows
          jobId={original.jobId}
          queue={original.queue}
          machineId={machineId}
          simulateExecution={simulateExecution}
          sharedIndex={sharedIndex}
          taskOrder={taskOrder}
          onSurgery={setSurgery}
          onForceComplete={setForceTarget}
          onConfirm={a => {
            setConfirmErr('');
            setConfirmBusy(false);
            setConfirmAction(a);
          }}
          productLabel={original.productLabel || original.name}
        />
      ),

      meta: { headerProps: { style: { width: '64%' } } }
    }
  ];

  const table = useAdvanceTable({
    data: rows,
    columns: machineQueueColumns,
    pageSize: 10,
    pagination: true,
    sortable: true
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <AdvanceTable
          tableProps={{ className: 'phoenix-table border-top border-200 fs-9' }}
        />
        <AdvanceTableFooter pagination />
      </AdvanceTableProvider>

      <ForceCompleteModal
        show={!!forceTarget}
        target={forceTarget}
        onHide={() => setForceTarget(null)}
      />

      <DrumSurgeryModal
        show={!!surgery}
        onHide={() => setSurgery(null)}
        jobId={surgery?.jobId}
        taskId={surgery?.taskId}
        machineId={machineId}
        // The handover's target is a DRUM on this line. Without the queue it has
        // nothing to offer and says "no other drum of this phase is queued",
        // which is what this screen did on 2026-10-22 with three of them on it.
        queueRows={rows}
      />

      {/* ONE CONFIRMATION for the two buttons that change production.
          `confirmLabel: null` makes it a message with no action — used for the
          409 refusal and for the version-skew warning, which are things to read
          rather than decisions to take. */}
      <Modal
        show={!!confirmAction}
        onHide={() => !confirmBusy && setConfirmAction(null)}
        centered
      >
        <Modal.Header closeButton={!confirmBusy}>
          <Modal.Title className="fs-7">{confirmAction?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmAction?.body && (
            <div className="fs-9 mb-2" style={{ whiteSpace: 'pre-line' }}>
              {confirmAction.body}
            </div>
          )}
          {!!confirmAction?.lines?.length && (
            <ul className="fs-9 mb-0 ps-3">
              {confirmAction.lines.map((l, i) => (
                <li key={i} className="mb-1">
                  {l}
                </li>
              ))}
            </ul>
          )}
          {confirmErr && (
            <div className="text-danger fs-9 mt-3" style={{ whiteSpace: 'pre-line' }}>
              {confirmErr}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={confirmBusy}
            onClick={() => setConfirmAction(null)}
          >
            {confirmAction?.confirmLabel ? 'Cancel' : 'Close'}
          </Button>
          {confirmAction?.confirmLabel && (
            <Button
              variant={confirmAction?.variant === 'warning' ? 'warning' : 'primary'}
              size="sm"
              disabled={confirmBusy}
              onClick={async () => {
                setConfirmBusy(true);
                setConfirmErr('');
                try {
                  await confirmAction.onConfirm?.();
                  setConfirmAction(null);
                } catch (err) {
                  // Kept OPEN on failure, with the reason. Closing it would
                  // leave the planner thinking it worked.
                  setConfirmErr(
                    err?.response?.data?.error || err?.message || 'Request failed'
                  );
                } finally {
                  setConfirmBusy(false);
                }
              }}
            >
              {confirmBusy ? 'Working…' : confirmAction.confirmLabel}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MachineQueueTable;
